import { SECURITY_KEY } from "../globals";

/**
 * Helper to fetch and determine the company limit for a user based on the MasterData table (MaxCompanies column).
 *
 * @param {string|number} [userId] - Optional userId. Defaults to localStorage.getItem("userId")
 * @returns {Promise<{
 *   maxCompanies: number|null,
 *   currentCount: number,
 *   canAddCompany: boolean,
 *   isLimitReached: boolean,
 *   isSuperAdmin: boolean
 * }>}
 */
export const fetchUserCompanyLimit = async (userId) => {
  const currentUserId = userId || localStorage.getItem("userId");
  const userType = localStorage.getItem("userType");
  const isSuperAdmin = String(userType) === "9";

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";
  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  let maxCompanies = null;
  let currentCount = 0;

  if (!currentUserId) {
    return {
      maxCompanies: null,
      currentCount: 0,
      canAddCompany: true,
      isLimitReached: false,
      isSuperAdmin: false,
    };
  }

  // 1. Try Node backend company limit endpoint
  try {
    const res = await fetch(`${BASE_API_URL}/company/limit/${currentUserId}`);
    if (res.ok) {
      const json = await res.json();
      if (json && json.success && json.data) {
        maxCompanies = Number(json.data.maxCompanies);
        currentCount = Number(json.data.currentCount);
        localStorage.setItem("maxCompanies", String(maxCompanies));

        const canAddCompany = isSuperAdmin || currentCount < maxCompanies;
        return {
          maxCompanies,
          currentCount,
          canAddCompany,
          isLimitReached: !isSuperAdmin && currentCount >= maxCompanies,
          isSuperAdmin,
        };
      }
    }
  } catch (err) {
    // Continue to fallback
  }

  // 2. Try Node backend profile endpoint for MaxCompanies
  try {
    const res = await fetch(`${BASE_API_URL}/profile/${currentUserId}`);
    if (res.ok) {
      const json = await res.json();
      if (json && json.success && json.data && json.data.MaxCompanies !== undefined && json.data.MaxCompanies !== null) {
        maxCompanies = Number(json.data.MaxCompanies);
        localStorage.setItem("maxCompanies", String(maxCompanies));
      }
    }
  } catch (err) {
    // Continue to REST API
  }

  // 3. Fallback to REST API SelectData.aspx for MasterData
  if (maxCompanies === null && BASE_REST_API_URL) {
    try {
      const postData = new URLSearchParams();
      postData.append("SecurityKey", SECURITY_KEY);
      postData.append("TableName", "masterdata");
      postData.append("WhereCondition", `ID=${currentUserId}`);
      postData.append("*", "*");

      const apiUrl = `${BASE_REST_API_URL.replace(/\/+$/, "")}/SelectData.aspx`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: postData.toString(),
      });

      const text = await response.text();
      const cleanText = text.replace(/^\uFEFF/, "").trim();
      const match = cleanText.match(/\{[\s\S]*\}/);
      if (match) {
        const json = JSON.parse(match[0]);
        if (json?.Response?.[0]?.MaxCompanies !== undefined && json.Response[0].MaxCompanies !== null) {
          maxCompanies = Number(json.Response[0].MaxCompanies);
          localStorage.setItem("maxCompanies", String(maxCompanies));
        }
      }
    } catch (err) {
      console.warn("REST API MasterData fetch warning:", err);
    }
  }

  // If still not resolved, check cached localStorage
  if (maxCompanies === null) {
    const cached = localStorage.getItem("maxCompanies");
    if (cached !== null && cached !== "") {
      maxCompanies = Number(cached);
    }
  }

  // 4. Fetch current company count if not yet known
  if (BASE_REST_API_URL) {
    try {
      const postData = new URLSearchParams();
      postData.append("SecurityKey", SECURITY_KEY);
      postData.append("TableName", "company");
      postData.append("WhereCondition", `MasterId=${currentUserId}`);
      postData.append("*", "*");

      const apiUrl = `${BASE_REST_API_URL.replace(/\/+$/, "")}/SelectData.aspx`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: postData.toString(),
      });

      const text = await response.text();
      const cleanText = text.replace(/^\uFEFF/, "").trim();
      const match = cleanText.match(/\{[\s\S]*\}/);
      if (match) {
        const json = JSON.parse(match[0]);
        if (Array.isArray(json?.Response)) {
          currentCount = json.Response.length;
        }
      }
    } catch (err) {
      console.warn("REST API company count fetch warning:", err);
    }
  }

  const effectiveLimit = maxCompanies !== null ? maxCompanies : 5;
  const canAddCompany = isSuperAdmin || currentCount < effectiveLimit;
  const isLimitReached = !isSuperAdmin && maxCompanies !== null && currentCount >= maxCompanies;

  return {
    maxCompanies,
    currentCount,
    canAddCompany,
    isLimitReached,
    isSuperAdmin,
  };
};
